import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/utils';
import ExportButton from '../components/ExportButton';
import LoanSummaryCards from '../components/loans/LoanSummaryCards';
import LoanCharts from '../components/loans/LoanCharts';
import { LoansTable } from '../components/loans/LoansTable';
import { RepaymentDialog } from '../components/loans/RepaymentDialog';
import { LoanScheduleDialog } from '../components/loans/LoanScheduleDialog';
import { LoanFormDialog } from '../components/loans/LoanFormDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_LOAN_FORM = {
  borrower_name: '', borrower_phone: '', business_type: '',
  loan_amount: '', interest_rate: '10', issue_date: new Date(), duration_months: '6',
  security: '', savings: '',
  guarantor_name: '', guarantor_relationship: '', guarantor_contact: '', guarantor_address: '',
};

const LoansPage = () => {
  const { hasRole } = useAuth();
  const [loans, setLoans] = useState([]);
  const [loanSummary, setLoanSummary] = useState(null);
  const [repayments, setRepayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [repaymentDialogOpen, setRepaymentDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [editingLoanId, setEditingLoanId] = useState(null);
  const [deleteLoanTarget, setDeleteLoanTarget] = useState(null);
  const [loanForm, setLoanForm] = useState({ ...EMPTY_LOAN_FORM, issue_date: new Date() });
  const [repaymentForm, setRepaymentForm] = useState({ amount: '', date: new Date(), notes: '' });

  const resetLoanForm = () => {
    setLoanForm({ ...EMPTY_LOAN_FORM, issue_date: new Date() });
    setEditingLoanId(null);
  };

  const openEditLoan = (loan) => {
    setEditingLoanId(loan.id);
    setLoanForm({
      borrower_name: loan.borrower_name || '',
      borrower_phone: loan.borrower_phone || '',
      business_type: loan.business_type || '',
      loan_amount: String(loan.loan_amount || ''),
      interest_rate: String(loan.interest_rate || '10'),
      issue_date: loan.issue_date ? new Date(loan.issue_date) : new Date(),
      duration_months: String(loan.duration_months || '6'),
      security: loan.security || '',
      savings: loan.savings ? String(loan.savings) : '',
      guarantor_name: loan.guarantor_name || '',
      guarantor_relationship: loan.guarantor_relationship || '',
      guarantor_contact: loan.guarantor_contact || '',
      guarantor_address: loan.guarantor_address || '',
    });
    setLoanDialogOpen(true);
  };

  const handleDeleteLoan = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/loans/${id}`);
      toast.success('Loan deleted');
      setDeleteLoanTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete loan');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [loansRes, summaryRes, repaymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/loans`),
        axios.get(`${API_URL}/api/reports/loan-summary`),
        axios.get(`${API_URL}/api/repayments`),
      ]);
      setLoans(loansRes.data);
      setLoanSummary(summaryRes.data);
      setRepayments(repaymentsRes.data);
    } catch (error) {
      toast.error('Failed to fetch loan data');
    } finally {
      setLoading(false);
    }
  };

  const handleLoanSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...loanForm,
        loan_amount: parseFloat(loanForm.loan_amount),
        interest_rate: parseFloat(loanForm.interest_rate),
        duration_months: parseInt(loanForm.duration_months),
        issue_date: format(loanForm.issue_date, 'yyyy-MM-dd'),
        savings: loanForm.savings ? parseFloat(loanForm.savings) : 0,
      };
      if (editingLoanId) {
        await axios.put(`${API_URL}/api/loans/${editingLoanId}`, payload);
        toast.success('Loan updated successfully');
      } else {
        await axios.post(`${API_URL}/api/loans`, payload);
        toast.success('Loan created successfully');
      }
      setLoanDialogOpen(false);
      resetLoanForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create loan');
    }
  };

  const handleRepaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLoan) return;

    try {
      await axios.post(`${API_URL}/api/repayments`, {
        loan_id: selectedLoan.id,
        amount: parseFloat(repaymentForm.amount),
        date: format(repaymentForm.date, 'yyyy-MM-dd'),
        notes: repaymentForm.notes,
      });
      toast.success('Repayment recorded successfully');
      setRepaymentDialogOpen(false);
      setRepaymentForm({ amount: '', date: new Date(), notes: '' });
      setSelectedLoan(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record repayment');
    }
  };

  const openRepaymentDialog = (loan) => {
    setSelectedLoan(loan);
    setRepaymentDialogOpen(true);
  };

  const openScheduleDialog = (loan) => {
    setSelectedLoan(loan);
    setScheduleDialogOpen(true);
  };

  const canEdit = hasRole(['admin', 'loan_officer']);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Women Entrepreneurship Loans</h2>
          <p className="text-muted-foreground">Manage micro-loans for women entrepreneurs</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton path="exports/list/loans" filenameBase="suubi_loans" testId="loans-export-btn" />
          {canEdit && (
          <LoanFormDialog
            open={loanDialogOpen}
            onOpenChange={(open) => { setLoanDialogOpen(open); if (!open) resetLoanForm(); }}
            form={loanForm}
            setForm={setLoanForm}
            onSubmit={handleLoanSubmit}
            editingId={editingLoanId}
            onTriggerClick={resetLoanForm}
          />
          )}
        </div>
      </div>

      {/* Hero Image */}
      <div
        className="h-40 rounded-xl bg-cover bg-center relative overflow-hidden"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1694286080811-e5e416f4fdb1?w=1200')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/40" />
        <div className="absolute inset-0 flex items-center p-8">
          <div className="text-white">
            <h3 className="font-heading text-2xl font-bold mb-2">Empowering Women Entrepreneurs</h3>
            <p className="text-white/80 max-w-lg">
              Supporting local businesses through accessible micro-loans
            </p>
          </div>
        </div>
      </div>

      <LoanSummaryCards summary={loanSummary} />

      <LoansTable
        loans={loans}
        loading={loading}
        canEdit={canEdit}
        onRepay={openRepaymentDialog}
        onEdit={openEditLoan}
        onDelete={setDeleteLoanTarget}
        onSchedule={openScheduleDialog}
      />

      <LoanCharts summary={loanSummary} />

      <RepaymentDialog
        open={repaymentDialogOpen}
        onOpenChange={setRepaymentDialogOpen}
        loan={selectedLoan}
        form={repaymentForm}
        setForm={setRepaymentForm}
        onSubmit={handleRepaymentSubmit}
      />

      <LoanScheduleDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        loan={selectedLoan}
        repayments={repayments}
      />

      {/* Delete Loan Confirmation */}
      <AlertDialog open={!!deleteLoanTarget} onOpenChange={(o) => { if (!o) setDeleteLoanTarget(null); }}>
        <AlertDialogContent data-testid="delete-loan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete loan?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteLoanTarget ? (
                <>Are you sure you want to delete the loan for{' '}
                <strong>{deleteLoanTarget.borrower_name}</strong>{' '}
                ({formatCurrency(deleteLoanTarget.loan_amount)})?{' '}
                This will also delete all associated repayments. This action cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteLoanTarget && handleDeleteLoan(deleteLoanTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-loan-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LoansPage;
